/**
 * Authentication guard component.
 *
 * Wraps protected routes and enforces authentication:
 * 1. If Clerk is still loading → shows the brand loader (pulsing logo)
 * 2. If not signed in → redirects to `/sign-in`
 * 3. If signed in but pending approval → redirects to `/pending-approval`
 * 4. If signed in but rejected/suspended → redirects to `/pending-approval`
 * 5. If signed in and approved → renders children
 *
 * The approval check calls `GET /api/v1/auth/status` with the Clerk JWT.
 * On first load the status is cached in the auth store to avoid repeated calls.
 *
 * @example
 * ```tsx
 * <AuthGuard>
 *   <ChatPage />
 * </AuthGuard>
 * ```
 */
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth, useUser } from '@clerk/clerk-react'
import { useReleaseKeysStore } from '@/stores/releaseKeysStore'
import { useAuthStore, resolveFallbackTenant } from '../shared/stores/auth'
import { useOnboardingStatusStore } from '../stores/onboardingStatusStore'
import { useChatStore, syncChatStoreIdentity } from '../stores/chatStore'
import { usePermissionsStore } from '../stores/permissionsStore'
import { BrandLoader } from '@/components/ui/brand-loader'
import { resetAuthErrorFlag } from '../lib/api'
import { setSentryIdentity } from '../lib/sentry'

interface AuthGuardProps {
  /** Protected content to render when authenticated and approved */
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const { user } = useUser()
  const navigate = useNavigate()
  const syncClerkState = useAuthStore((s) => s.syncClerkState)
  const { t } = useTranslation()
  const approvalStatus = useAuthStore((s) => s.approvalStatus)
  // #86 — falha de TRANSPORTE em /auth/status: aviso não bloqueante, sem redirecionar.
  const approvalCheckDegraded = useAuthStore((s) => s.approvalCheckDegraded) === true
  const checkApprovalStatus = useAuthStore((s) => s.checkApprovalStatus)
  const fetchOnboardingStatus = useOnboardingStatusStore((s) => s.fetchStatus)
  const fetchPermissions = usePermissionsStore((s) => s.fetchPermissions)
  const tenantId = useAuthStore((s) => s.tenantId)
  const userId = useAuthStore((s) => s.userId)
  const activeConversationId = useChatStore((s) => s.activeConversationId)

  // Sync Clerk state to Zustand auth store and reset the api.ts auth-error guard
  // so requests can proceed after the user re-authenticates.
  //
  // AxiomGO is B2C — users do NOT belong to a Clerk organization. The tenant is
  // owned by the engine: `requireAuth` derives it from the authenticated user
  // (clerk-auth.ts) and IGNORES the `X-Tenant-Id` header on the authenticated
  // (prod) path. So the frontend tenant value is only the dev single-tenant id
  // (`resolveFallbackTenant()`, empty in PROD) and, in PROD, the Clerk user id —
  // a non-empty placeholder that satisfies api.ts' guard and is ignored server
  // side. There is no Clerk org gate: an authenticated user always proceeds.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return

    const tenantId = resolveFallbackTenant() || user.id
    syncClerkState(user.id, tenantId, getToken)
    // SOC-011 — re-chaveia e re-hidrata o retrato do chat pela identidade que
    // acabou de ser resolvida (login ou troca de conta no mesmo navegador).
    void syncChatStoreIdentity(tenantId, user.id)
    resetAuthErrorFlag()
  }, [isLoaded, isSignedIn, user, syncClerkState, getToken])

  // STO-2157 — a chave de disco do retrato do chat é o INQUILINO REAL, que só
  // aparece quando `/auth/status` responde. O efeito acima sincroniza com o
  // placeholder do Clerk (`user.id`); quando o motor devolve o UUID real do
  // inquilino, `checkApprovalStatus` sobrescreve `tenantId` e este efeito
  // re-chaveia e re-hidrata sob a chave correta. Sem isto, `identityScopedStorage`
  // grava e lê o aviso de inatividade sob duas chaves diferentes e ele some no
  // reload da mesma conversa.
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user || !tenantId) return
    void syncChatStoreIdentity(tenantId, user.id)
  }, [tenantId, isLoaded, isSignedIn, user])

  // T5 (OBS-1): keep the Sentry scope carrying WHO hit each error — tenant,
  // user and the conversation currently open in the chat. Runs whenever any
  // of the three changes, so an error thrown mid-conversation is attributed
  // correctly even when it never goes through `captureException()`.
  useEffect(() => {
    setSentryIdentity({ userId: user?.id, tenantId, conversationId: activeConversationId })
  }, [user?.id, tenantId, activeConversationId])

  // Check approval status after sync.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    if (approvalStatus === 'unknown') {
      void checkApprovalStatus()
    }
  }, [isLoaded, isSignedIn, approvalStatus, checkApprovalStatus])

  // Chaves de liberação (ex.: `comando_de_voz`): carregadas depois do login,
  // uma vez por sessão aprovada. Falha de rede = tudo desligado (falha fechada).
  useEffect(() => {
    if (approvalStatus !== 'approved') {
      useReleaseKeysStore.getState().reset()
      return
    }
    void useReleaseKeysStore.getState().load()
  }, [approvalStatus, userId])

  // Fetch onboarding status once the user is approved so ChatPage can
  // proactively show the OnboardingCard for new tenants.
  useEffect(() => {
    if (approvalStatus !== 'approved') return
    void fetchOnboardingStatus()
  }, [approvalStatus, fetchOnboardingStatus])

  // STO-242 — busca `GET /api/v1/permissions/me` assim que a sessão é
  // aprovada, para que `usePermission()` tenha o `enforceMode`/lista real
  // disponível antes de qualquer card de ação renderizar. Falha de rede cai
  // em `unavailable`: sessões identificadas ficam somente leitura até uma
  // resposta canônica atual. Troca de sujeito exige nova consulta.
  useEffect(() => {
    if (approvalStatus !== 'approved') return
    void fetchPermissions()
  }, [approvalStatus, fetchPermissions, tenantId, userId])

  // Redirect unauthenticated users
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/sign-in', { replace: true })
    }
  }, [isLoaded, isSignedIn, navigate])

  // Redirect pending users
  useEffect(() => {
    if (approvalStatus === 'pending') {
      navigate('/pending-approval', { replace: true })
    }
  }, [approvalStatus, navigate])

  // Redirect rejected/suspended users. `normalizeApprovalStatus` maps both the
  // engine "REJECTED" and "SUSPENDED" enums to 'rejected' — neither may render
  // the app. Without this branch a rejected/suspended account fell through to
  // `return <>{children}</>` and saw the full SPA (security gap). There is no
  // dedicated rejected route, so reuse /pending-approval (it polls auth/status
  // and only releases to /chat when the engine returns 'approved').
  useEffect(() => {
    if (approvalStatus === 'rejected') {
      navigate('/pending-approval', { replace: true })
    }
  }, [approvalStatus, navigate])

  // Loading state
  if (!isLoaded) {
    return <BrandLoader className="h-screen bg-axg-bg" />
  }

  // Not signed in — will redirect via useEffect
  if (!isSignedIn) {
    return null
  }

  const degradedNotice = approvalCheckDegraded ? (
    <p
      role="status"
      data-testid="auth-status-degraded"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 mx-auto w-fit max-w-[calc(100%-32px)] rounded-md bg-axg-surface-2 px-3 py-2 text-center text-sm text-axg-secondary shadow"
    >
      {t('auth.statusUnavailable')}
    </p>
  ) : null

  // Approval check in progress (#86: transport failure with no status yet = loading)
  if (approvalStatus === 'unknown') {
    return (
      <>
        <BrandLoader className="h-screen bg-axg-bg" />
        {degradedNotice}
      </>
    )
  }

  // Pending — will redirect via useEffect
  if (approvalStatus === 'pending') {
    return null
  }

  // Rejected/suspended — will redirect via useEffect. Must NOT fall through to
  // rendering the app.
  if (approvalStatus === 'rejected') {
    return null
  }

  return (
    <>
      {children}
      {degradedNotice}
    </>
  )
}
